import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import type {
  DayFocus,
  TaskPlan,
  TodoProject,
  TodoProjectColorKey,
  TodoSwimLane,
} from '../types';
import { isFocusFullyCompleted, isTaskListFullyCompleted } from '../utils/todoSwimLanes';

const paths = {
  projects: (userId: string) => collection(getFirebaseDb(), 'users', userId, 'todoProjects'),
  project: (userId: string, projectId: string) =>
    doc(getFirebaseDb(), 'users', userId, 'todoProjects', projectId),
  task: (userId: string, taskId: string) => doc(getFirebaseDb(), 'users', userId, 'tasks', taskId),
  focus: (userId: string, focusId: string) =>
    doc(getFirebaseDb(), 'users', userId, 'dayFocus', focusId),
};

/** Firestore writeBatch hard limit is 500 ops; keep headroom for serverTimestamps and side writes. */
const CASCADE_BATCH_LIMIT = 400;

const NAME_MIN = 1;
const NAME_MAX = 120;

export function normalizeProjectName(input: string): string {
  return input.replace(/\s+/g, ' ').trim();
}

export function isValidProjectName(input: string): boolean {
  const value = normalizeProjectName(input);
  return value.length >= NAME_MIN && value.length <= NAME_MAX;
}

export function normalizeTodoProject(id: string, raw: unknown): TodoProject {
  const data = (raw && typeof raw === 'object') ? raw as Record<string, unknown> : {};
  const colorKey = data.colorKey as TodoProjectColorKey | undefined;
  return {
    id,
    userId: typeof data.userId === 'string' ? data.userId : '',
    name: typeof data.name === 'string' ? data.name : 'Untitled project',
    colorKey,
    archived: Boolean(data.archived),
    pinned: Boolean(data.pinned),
    createdAt: data.createdAt as TodoProject['createdAt'],
    updatedAt: data.updatedAt as TodoProject['updatedAt'],
  };
}

export async function createTodoProject(
  userId: string,
  input: { name: string; colorKey?: TodoProjectColorKey }
): Promise<string> {
  const name = normalizeProjectName(input.name);
  if (!isValidProjectName(name)) {
    throw new Error('Project name must be between 1 and 120 characters.');
  }
  const now = Timestamp.now();
  // NOTE: Firestore rejects `undefined` field values (we don't enable
  // `ignoreUndefinedProperties` on the SDK init). Only include `colorKey`
  // when the caller actually picked one.
  const data: Record<string, unknown> = {
    userId,
    name,
    archived: false,
    pinned: false,
    createdAt: now,
    updatedAt: now,
  };
  if (input.colorKey) data.colorKey = input.colorKey;
  const ref = await addDoc(paths.projects(userId), data);
  return ref.id;
}

export async function renameTodoProject(
  userId: string,
  projectId: string,
  rawName: string
): Promise<void> {
  const name = normalizeProjectName(rawName);
  if (!isValidProjectName(name)) {
    throw new Error('Project name must be between 1 and 120 characters.');
  }
  await updateDoc(paths.project(userId, projectId), {
    name,
    updatedAt: Timestamp.now(),
  });
}

export async function setTodoProjectColor(
  userId: string,
  projectId: string,
  colorKey: TodoProjectColorKey | null
): Promise<void> {
  await updateDoc(paths.project(userId, projectId), {
    colorKey: colorKey ?? deleteField(),
    updatedAt: Timestamp.now(),
  });
}

export async function setTodoProjectArchived(
  userId: string,
  projectId: string,
  archived: boolean
): Promise<void> {
  await updateDoc(paths.project(userId, projectId), {
    archived,
    updatedAt: Timestamp.now(),
  });
}

export async function setTodoProjectPinned(
  userId: string,
  projectId: string,
  pinned: boolean
): Promise<void> {
  await updateDoc(paths.project(userId, projectId), {
    pinned,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Hard-deletes a `TodoProject` document.
 *
 * UI must block this when ≥1 card still references the project (see PRD §8.8 / §13).
 * This service does not enforce the rule — callers should use {@link countCardsReferencingProject}
 * + {@link clearProjectFromAllCards} or a reassign flow first.
 */
export async function deleteTodoProject(userId: string, projectId: string): Promise<void> {
  await deleteDoc(paths.project(userId, projectId));
}

export async function countCardsReferencingProject(
  userId: string,
  projectId: string
): Promise<{ tasks: number; focuses: number }> {
  const [taskSnap, focusSnap] = await Promise.all([
    getDocs(query(collection(getFirebaseDb(), 'users', userId, 'tasks'), where('projectId', '==', projectId))),
    getDocs(query(collection(getFirebaseDb(), 'users', userId, 'dayFocus'), where('projectId', '==', projectId))),
  ]);
  return { tasks: taskSnap.size, focuses: focusSnap.size };
}

/**
 * Clears `projectId` from every card pointing at this project (Move-to-Inbox flow).
 * Returns the IDs touched in case the caller wants to log or retry.
 */
export async function clearProjectFromAllCards(
  userId: string,
  projectId: string
): Promise<{ taskIds: string[]; focusIds: string[] }> {
  const [taskSnap, focusSnap] = await Promise.all([
    getDocs(query(collection(getFirebaseDb(), 'users', userId, 'tasks'), where('projectId', '==', projectId))),
    getDocs(query(collection(getFirebaseDb(), 'users', userId, 'dayFocus'), where('projectId', '==', projectId))),
  ]);

  const taskIds = taskSnap.docs.map((d) => d.id);
  const focusIds = focusSnap.docs.map((d) => d.id);

  const all: Array<{ ref: ReturnType<typeof paths.task> }> = [
    ...taskIds.map((id) => ({ ref: paths.task(userId, id) })),
    ...focusIds.map((id) => ({ ref: paths.focus(userId, id) })),
  ];

  await commitInBatches(all, (batch, entry) => {
    batch.update(entry.ref, {
      projectId: deleteField(),
      updatedAt: Timestamp.now(),
    });
  });

  return { taskIds, focusIds };
}

// ─── Cascade ────────────────────────────────────────────────────────────────

/**
 * One entry in the cascade result, used to drive Undo (`todos-project-pivot-prd.md` §7.3).
 * `prevLane` of `null` means the field was unset; restoring it should call `deleteField`.
 */
export interface CascadeAffectedCard {
  kind: 'task' | 'focus';
  id: string;
  prevLane: TodoSwimLane | null;
}

export interface CascadeOptions {
  /** Default false — completed lists are preserved unless explicitly opted in. */
  includeCompleted?: boolean;
}

/**
 * Applies the deterministic cascade described in PRD §7.2 against client-side caches.
 * Returns the affected card descriptors so the caller can issue an Undo via {@link restoreCardSwimLanes}.
 */
export async function cascadeProjectSwimLane(
  userId: string,
  projectId: string,
  targetLane: TodoSwimLane,
  inputs: { tasks: TaskPlan[]; focuses: DayFocus[] },
  options?: CascadeOptions
): Promise<CascadeAffectedCard[]> {
  const includeCompleted = Boolean(options?.includeCompleted);

  const affected: CascadeAffectedCard[] = [];
  type CascadeUpdate = { ref: ReturnType<typeof paths.task>; lane: TodoSwimLane };
  const updates: CascadeUpdate[] = [];

  for (const t of inputs.tasks) {
    if (t.projectId !== projectId) continue;
    if (!includeCompleted && isTaskListFullyCompleted(t)) continue;
    affected.push({ kind: 'task', id: t.id, prevLane: t.swimLane ?? null });
    updates.push({ ref: paths.task(userId, t.id), lane: targetLane });
  }
  for (const f of inputs.focuses) {
    if (f.projectId !== projectId) continue;
    if (!includeCompleted && isFocusFullyCompleted(f)) continue;
    affected.push({ kind: 'focus', id: f.id, prevLane: f.swimLane ?? null });
    updates.push({ ref: paths.focus(userId, f.id), lane: targetLane });
  }

  if (updates.length === 0) return [];

  await commitInBatches(updates, (batch, entry) => {
    batch.update(entry.ref, { swimLane: entry.lane, updatedAt: Timestamp.now() });
  });

  return affected;
}

/**
 * Best-effort Undo for a cascade — see PRD §7.3.
 * Cards deleted between the cascade and the Undo are skipped silently.
 */
export async function restoreCardSwimLanes(
  userId: string,
  affected: CascadeAffectedCard[]
): Promise<void> {
  if (affected.length === 0) return;
  type RestoreUpdate = { ref: ReturnType<typeof paths.task>; prevLane: TodoSwimLane | null };
  const updates: RestoreUpdate[] = affected.map((entry) => ({
    ref: entry.kind === 'task'
      ? paths.task(userId, entry.id)
      : paths.focus(userId, entry.id),
    prevLane: entry.prevLane,
  }));

  await commitInBatches(updates, (batch, entry) => {
    batch.update(entry.ref, {
      swimLane: entry.prevLane ?? deleteField(),
      updatedAt: Timestamp.now(),
    });
  }, { ignoreNotFound: true });
}

// ─── Internal helpers ───────────────────────────────────────────────────────

async function commitInBatches<T>(
  entries: T[],
  applyOp: (batch: ReturnType<typeof writeBatch>, entry: T) => void,
  options?: { ignoreNotFound?: boolean }
): Promise<void> {
  if (entries.length === 0) return;
  const db = getFirebaseDb();
  for (let i = 0; i < entries.length; i += CASCADE_BATCH_LIMIT) {
    const slice = entries.slice(i, i + CASCADE_BATCH_LIMIT);
    const batch = writeBatch(db);
    for (const entry of slice) applyOp(batch, entry);
    try {
      await batch.commit();
    } catch (err) {
      if (options?.ignoreNotFound) {
        // Best-effort path (Undo) — swallow per-entry races.
        if (typeof console !== 'undefined') {
          console.warn('[todoProjects] partial commit failure (ignored for undo):', err);
        }
        continue;
      }
      throw err;
    }
  }
}
